import React, { useState, useEffect, useCallback } from 'react';
import './CookieConsent.css';

/* ═══════════════════════════════════════════════════════════════
   CookieConsent — GDPR-compliant banner + preferences modal.
   Controls loading of Google Analytics & Facebook Pixel based
   on user consent.
   ═══════════════════════════════════════════════════════════════ */

const CONSENT_KEY = 'intertennis_cookie_consent';

const DEFAULT_CONSENT = {
    essential: true,      // always on
    analytics: false,     // Google Analytics
    advertising: false,   // Facebook Pixel
};

/* ── Read / write helpers ── */
const getStoredConsent = () => {
    try {
        const raw = localStorage.getItem(CONSENT_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const storeConsent = (consent) => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    // Also set a simple cookie so server-side can read consent status
    const expires = new Date(Date.now() + 365 * 864e5).toUTCString();
    document.cookie = `${CONSENT_KEY}=true; expires=${expires}; path=/; SameSite=Lax`;
};

/* ── Script loaders — called once when consent is granted ── */
const loadGoogleAnalytics = () => {
    if (document.querySelector('script[src*="googletagmanager.com/gtag"]')) return;

    const tag = document.createElement('script');
    tag.async = true;
    tag.src = 'https://www.googletagmanager.com/gtag/js?id=G-D8ZBRQSKVQ';
    document.head.appendChild(tag);

    const cfg = document.createElement('script');
    cfg.textContent = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){ dataLayer.push(arguments); }
        gtag('js', new Date());
        gtag('config', 'G-D8ZBRQSKVQ');
    `;
    document.head.appendChild(cfg);
};

const loadFacebookPixel = () => {
    if (window.fbq && window.fbq.loaded) return;

    (function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
        t = b.createElement(e); t.async = true;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    window.fbq('init', '1415640272421410');
    window.fbq('track', 'PageView');
};

const applyConsent = (consent) => {
    if (consent.analytics)   loadGoogleAnalytics();
    if (consent.advertising) loadFacebookPixel();
};

/* ═══════════════════════════════════════════════════════════════ */

const CookieConsent = () => {
    const [visible, setVisible] = useState(false);     // banner
    const [showPrefs, setShowPrefs] = useState(false);  // modal
    const [prefs, setPrefs] = useState({ ...DEFAULT_CONSENT });

    /* On mount: if consent exists, apply it silently. Otherwise show banner. */
    useEffect(() => {
        // Don't show on signup flows
        const params = new URLSearchParams(window.location.search);
        if (params.get('signup') === 'true' || params.get('signup') === 'ios' || params.get('signup') === 'android') {
            return;
        }

        const stored = getStoredConsent();
        if (stored) {
            setPrefs(stored);
            applyConsent(stored);
        } else {
            setVisible(true);
        }
    }, []);

    /* ── Actions ── */
    const acceptAll = useCallback(() => {
        const consent = { essential: true, analytics: true, advertising: true };
        storeConsent(consent);
        setPrefs(consent);
        applyConsent(consent);
        setVisible(false);
        setShowPrefs(false);
    }, []);

    const declineAll = useCallback(() => {
        const consent = { ...DEFAULT_CONSENT };
        storeConsent(consent);
        setPrefs(consent);
        setVisible(false);
        setShowPrefs(false);
    }, []);

    const savePrefs = useCallback(() => {
        storeConsent(prefs);
        applyConsent(prefs);
        setVisible(false);
        setShowPrefs(false);
    }, [prefs]);

    const togglePref = (key) => {
        setPrefs(prev => ({ ...prev, [key]: !prev[key] }));
    };

    /* ── Nothing to render ── */
    if (!visible && !showPrefs) return null;

    /* ── Preferences modal ── */
    if (showPrefs) {
        return (
            <div className="cc-overlay" onClick={() => setShowPrefs(false)}>
                <div className="cc-modal" onClick={e => e.stopPropagation()}>
                    <div className="cc-modal__header">
                        <span className="cc-modal__title">Cookie Preferences</span>
                        <button className="cc-modal__close" onClick={() => setShowPrefs(false)}>✕</button>
                    </div>

                    <p className="cc-modal__desc">
                        We use cookies to enhance your browsing experience, serve personalized ads, and analyze our traffic.
                        You can choose which categories to allow. Read our{' '}
                        <a href="/cookie">Cookie Policy</a> and{' '}
                        <a href="/privacy">Privacy Policy</a> for more details.
                    </p>

                    {/* Essential */}
                    <div className="cc-category">
                        <div className="cc-category__info">
                            <div className="cc-category__name">Essential</div>
                            <div className="cc-category__desc">
                                Required for the site to function. Cannot be disabled.
                            </div>
                        </div>
                        <span className="cc-category__always">Always On</span>
                    </div>

                    {/* Analytics */}
                    <div className="cc-category">
                        <div className="cc-category__info">
                            <div className="cc-category__name">Analytics</div>
                            <div className="cc-category__desc">
                                Google Analytics — helps us understand how visitors use the site.
                            </div>
                        </div>
                        <label className="cc-toggle">
                            <input
                                type="checkbox"
                                checked={prefs.analytics}
                                onChange={() => togglePref('analytics')}
                            />
                            <span className="cc-toggle__track" />
                        </label>
                    </div>

                    {/* Advertising */}
                    <div className="cc-category">
                        <div className="cc-category__info">
                            <div className="cc-category__name">Advertising</div>
                            <div className="cc-category__desc">
                                Facebook Pixel — used to measure ad performance and deliver relevant ads.
                            </div>
                        </div>
                        <label className="cc-toggle">
                            <input
                                type="checkbox"
                                checked={prefs.advertising}
                                onChange={() => togglePref('advertising')}
                            />
                            <span className="cc-toggle__track" />
                        </label>
                    </div>

                    <div className="cc-modal__footer">
                        <button className="cc-btn cc-btn--save" onClick={savePrefs}>
                            Save Preferences
                        </button>
                        <button className="cc-btn cc-btn--accept-all" onClick={acceptAll}>
                            Accept All
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    /* ── Compact banner ── */
    return (
        <div className="cc-banner">
            <div className="cc-banner__text">
                We use cookies to enhance your experience and analyze traffic.
                Read our <a href="/privacy">Privacy Policy</a> and <a href="/cookie">Cookie Policy</a>.
            </div>
            <div className="cc-banner__actions">
                <button className="cc-btn cc-btn--manage" onClick={() => setShowPrefs(true)}>
                    Manage
                </button>
                <button className="cc-btn cc-btn--decline" onClick={declineAll}>
                    Decline
                </button>
                <button className="cc-btn cc-btn--accept" onClick={acceptAll}>
                    Accept All
                </button>
            </div>
        </div>
    );
};

export default CookieConsent;
